import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  Car, 
  LayoutDashboard, 
  FileText, 
  AlertTriangle, 
  History,
  LogOut,
  Loader2,
  Users,
  RefreshCw,
  Building,
  Store,
  Gift,
  BarChart3,
  Wallet,
  DollarSign,
  Package
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { ParkWiseLogo } from "../../components/ui/parkwise-logo";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import axios from "axios";

const API = '/api';

export default function AdminAuditLog() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      fetchLogs();
    } catch (error) {
      navigate("/admin/login");
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/audit-logs`, {
        withCredentials: true
      });
      setLogs(response.data);
    } catch (error) {
      console.error("Failed to fetch logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    navigate("/admin/login");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getActionBadge = (action) => {
    const colors = {
      status_change: "bg-blue-500/20 text-blue-400",
      ocr_manual_approve: "bg-green-500/20 text-green-400",
      create: "bg-emerald-500/20 text-emerald-400",
      delete: "bg-red-500/20 text-red-400"
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${colors[action] || "bg-slate-500/20 text-slate-400"}`}>
        {action.replace(/_/g, ' ')}
      </span>
    );
  };

  return (
    <div className="admin-container min-h-screen" data-testid="admin-audit-log">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-slate-900 border-r border-slate-800 p-6">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-600 to-green-500 flex items-center justify-center">
            <Car className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            ParkWise
          </span>
        </div>

        <nav className="space-y-2">
          <Link 
            to="/admin/dashboard" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
          >
            <LayoutDashboard className="w-5 h-5" />
            <span>Dashboard</span>
          </Link>
          <Link 
            to="/admin/registrations" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
          >
            <FileText className="w-5 h-5" />
            <span>Registrations</span>
          </Link>
          <Link 
            to="/admin/sites" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
          >
            <Building className="w-5 h-5" />
            <span>Sites & Kiosks</span>
          </Link>
          <Link 
            to="/admin/retailers" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
          >
            <Store className="w-5 h-5" />
            <span>Retailers</span>
          </Link>
          <Link 
            to="/admin/rewards" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
          >
            <Gift className="w-5 h-5" />
            <span>Rewards</span>
          </Link>
          <Link 
            to="/admin/analytics" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
          >
            <BarChart3 className="w-5 h-5" />
            <span>Analytics</span>
          </Link>
          <Link 
            to="/admin/ocr-exceptions" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
          >
            <AlertTriangle className="w-5 h-5" />
            <span>OCR Exceptions</span>
          </Link>
          <Link 
            to="/admin/audit-log" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg bg-slate-800 text-white"
          >
            <History className="w-5 h-5" />
            <span>Audit Log</span>
          </Link>
          
          {/* Commercial Section */}
          <div className="pt-4 mt-4 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Commercial</p>
            <Link to="/admin/commercial" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <Wallet className="w-5 h-5" /><span>Commercial Dashboard</span>
            </Link>
            <Link to="/admin/commercial/finance" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <DollarSign className="w-5 h-5" /><span>Finance Centre</span>
            </Link>
            <Link to="/admin/commercial/products-tariffs" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <Package className="w-5 h-5" /><span>Products & Tariffs</span>
            </Link>
          </div>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <div className="flex items-center gap-3 mb-4">
            <ParkWiseLogo size="sm" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-slate-400 hover:text-white text-sm w-full"
          >
            <LogOut className="w-4 h-4" />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="ml-64 p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 
              className="text-3xl font-bold text-white mb-2"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Audit Log
            </h1>
            <p className="text-slate-400">
              History of administrative actions
            </p>
          </div>
          <Button
            variant="outline"
            onClick={fetchLogs}
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
            data-testid="refresh-button"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        <div className="admin-card border-slate-800 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <History className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No audit logs yet</p>
            </div>
          ) : (
            <Table className="admin-table">
              <TableHeader>
                <TableRow className="border-slate-800 hover:bg-transparent">
                  <TableHead className="text-slate-400">Timestamp</TableHead>
                  <TableHead className="text-slate-400">User</TableHead>
                  <TableHead className="text-slate-400">Action</TableHead>
                  <TableHead className="text-slate-400">Entity</TableHead>
                  <TableHead className="text-slate-400">Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.audit_id} className="border-slate-800/50 hover:bg-slate-800/30">
                    <TableCell className="text-slate-400 text-sm">
                      {formatDate(log.timestamp)}
                    </TableCell>
                    <TableCell className="text-slate-300">{log.user_id}</TableCell>
                    <TableCell>{getActionBadge(log.action)}</TableCell>
                    <TableCell className="text-slate-300">
                      <span className="text-slate-500">{log.entity_type}:</span>{" "}
                      <span className="font-mono text-xs">{log.entity_id}</span>
                    </TableCell>
                    <TableCell className="text-slate-400 text-sm max-w-xs truncate">
                      {log.details ? JSON.stringify(log.details) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>
    </div>
  );
}
